import { Field } from '@web/components/atoms/Field'
import { Input } from '@web/components/atoms/Input'
import { Spinner } from '@web/components/atoms/Spinner'
import { Box, Center, HStack, VStack } from '@web/components/atoms/Layout'
import { LoginFormSubcomponents, type LoginForm as LoginFormProps } from '@web/graphql-components'
import { PageButton } from '../PageButton'
import { SubComponentProps } from '../utils'
import { useColorWithOpacity } from '@web/components/molecules/ColorPicker/utils'
import { DEFAULT_LOGIN_FORM } from '../default'
import { Body2, Section2 } from '@web/components/atoms/Typography'
import { CircleTickIcon, Icon } from '@web/components/atoms/Icons'

export enum LoginFormStep {
  Login,
  Authenticating,
  SentEmail,
  Success,
}

const LoginForm = ({
  id,
  styles,
  mobileStyles,
  SubComponent,
  subComponents,
  onSubmit,
  loading,
  step = LoginFormStep.Login,
  error,
  ...rest
}: LoginFormProps & {
  step?: LoginFormStep
  loading?: boolean
  error?: string
  onSubmit?: (e: React.FormEvent) => void
  SubComponent: React.FC<SubComponentProps>
}) => {
  const components = subComponents as LoginFormSubcomponents
  const backgroundColor = useColorWithOpacity(
    styles?.backgroundColor,
    styles?.backgroundColorOpacity,
    DEFAULT_LOGIN_FORM.styles.backgroundColor,
  )
  const borderColor = useColorWithOpacity(
    styles?.borderColor,
    styles?.borderColorOpacity,
    DEFAULT_LOGIN_FORM.styles.borderColor,
  )
  const textColor = useColorWithOpacity(styles?.textColor, '1', DEFAULT_LOGIN_FORM.styles.textColor)

  return (
    <VStack
      w="100%"
      justifyContent="center"
      alignItems="center"
      asChild={true}
      gap="24px"
      bgColor={backgroundColor}
      borderRadius={`${styles?.borderRadius}px`}
      borderColor={borderColor}
      color={textColor}
      css={{
        p: mobileStyles?.padding,
        '@container page (min-width: 901px)': {
          p: styles?.padding,
        },
      }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()

          onSubmit?.(e)
        }}
      >
        {step === LoginFormStep.Login && (
          <>
            {styles?.hasTitle && <SubComponent {...rest} id={components.title!} name="Title" parentId={id} />}
            <Field label="Email Address" required={true}>
              <Input id="email" name="email" type="email" placeholder="Email" />
            </Field>
            <PageButton type="submit" name="Primary" buttonText="Log in" width="100%" isLoading={loading} />
            <Body2 color="ge.text.danger">{error}</Body2>
          </>
        )}
        {step === LoginFormStep.Authenticating && (
          <Center w="100%" h="100%">
            <Spinner />
          </Center>
        )}
        {step === LoginFormStep.SentEmail && (
          <Center w="100%" h="100%">
            <HStack>
              <Icon boxSize="24px">
                <CircleTickIcon />
              </Icon>
              <Box>
                <Section2>Check your email</Section2>
                <Body2>Check your email for a link to log in. If you don&apos;t see it, check your spam folder.</Body2>
              </Box>
            </HStack>
          </Center>
        )}
      </form>
    </VStack>
  )
}

export default LoginForm
